import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/axios';

export const fetchStationOrders = createAsyncThunk(
  'kds/fetchStationOrders',
  async ({ stationId, allItems = false }, { rejectWithValue }) => {
    try {
      const params = allItems ? { all_items: 1 } : {};
      const res = await api.get(`/v1/kds/stations/${stationId}/orders`, { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch station orders');
    }
  }
);

export const fetchAllKdsOrders = createAsyncThunk(
  'kds/fetchAllKdsOrders',
  async ({ locationId, status } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (locationId) params.location_id = locationId;
      if (status) params.status = status;
      const res = await api.get('/v1/kds/orders', { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch KDS orders');
    }
  }
);

export const fetchKitchenStations = createAsyncThunk(
  'kds/fetchKitchenStations',
  async (locationId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/v1/locations/${locationId}/kitchen-stations`);
      return res.data.stations;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch stations');
    }
  }
);

export const toggleItemDone = createAsyncThunk(
  'kds/toggleItemDone',
  async ({ orderId, itemIndex, done, stationId }, { dispatch, rejectWithValue }) => {
    dispatch(optimisticToggleItem({ orderId, itemIndex, done }));
    try {
      const res = await api.post(`/v1/kds/orders/${orderId}/toggle-item`, {
        item_index: itemIndex,
        done,
        station_id: stationId || null,
      });
      return { orderId, ...res.data };
    } catch (err) {
      dispatch(optimisticToggleItem({ orderId, itemIndex, done: !done }));
      return rejectWithValue(err.response?.data?.message || 'Failed to toggle item');
    }
  }
);

export const completeStation = createAsyncThunk(
  'kds/completeStation',
  async ({ orderId, stationId }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/v1/kds/orders/${orderId}/complete-station`, {
        station_id: stationId,
      });
      return { orderId, ...res.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to complete station');
    }
  }
);

export const markDelivered = createAsyncThunk(
  'kds/markDelivered',
  async (orderId, { rejectWithValue }) => {
    try {
      const res = await api.post(`/v1/kds/orders/${orderId}/delivered`);
      return { orderId, ...res.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to mark delivered');
    }
  }
);

export const recallOrder = createAsyncThunk(
  'kds/recallOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const res = await api.post(`/v1/kds/orders/${orderId}/recall`);
      return { orderId, ...res.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to recall order');
    }
  }
);

const kdsSlice = createSlice({
  name: 'kds',
  initialState: {
    orders: [],
    stations: [],
    currentStation: null,
    loading: false,
    error: null,
    newOrderFlag: 0,
  },
  reducers: {
    upsertOrder(state, action) {
      const incoming = action.payload;
      if (incoming.items) {
        const statuses = incoming.item_statuses || [];
        incoming.items = incoming.items.map((item, i) => ({
          ...item,
          index: item.index ?? i,
          done: item.done ?? (statuses[item.index ?? i]?.done || false),
        }));
      }
      const idx = state.orders.findIndex((o) => o.id === incoming.id);
      if (idx >= 0) {
        state.orders[idx] = { ...state.orders[idx], ...incoming };
      } else {
        state.newOrderFlag = Date.now();
      }
    },
    removeOrder(state, action) {
      state.orders = state.orders.filter((o) => o.id !== action.payload);
    },
    updateOrderItemStatuses(state, action) {
      const { orderId, item_statuses, station_progress, status } = action.payload;
      const order = state.orders.find((o) => o.id === orderId);
      if (order) {
        order.item_statuses = item_statuses;
        order.station_progress = station_progress;
        if (status) order.status = status;
      }
    },
    optimisticToggleItem(state, action) {
      const { orderId, itemIndex, done } = action.payload;
      const order = state.orders.find((o) => o.id === orderId);
      if (!order || !order.items) return;
      const item = order.items.find((it) => (it.index != null ? it.index : order.items.indexOf(it)) === itemIndex);
      if (item) item.done = done;
    },
    clearKds(state) {
      state.orders = [];
      state.stations = [];
      state.currentStation = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStationOrders.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchStationOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.orders;
        state.currentStation = action.payload.station;
      })
      .addCase(fetchStationOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchAllKdsOrders.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchAllKdsOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.orders;
        state.stations = action.payload.stations || state.stations;
      })
      .addCase(fetchAllKdsOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchKitchenStations.fulfilled, (state, action) => {
        state.stations = action.payload;
      })

      .addCase(toggleItemDone.fulfilled, (state, action) => {
        const { orderId, item_statuses, station_progress, status } = action.payload;
        const order = state.orders.find((o) => o.id === orderId);
        if (order) {
          order.item_statuses = item_statuses;
          order.station_progress = station_progress;
          if (status) order.status = status;
          if (order.items && item_statuses) {
            order.items.forEach((item) => {
              const idx = item.index ?? order.items.indexOf(item);
              item.done = item_statuses[idx]?.done || false;
            });
          }
        }
      })

      .addCase(completeStation.fulfilled, (state, action) => {
        const { orderId, item_statuses, station_progress, status } = action.payload;
        const order = state.orders.find((o) => o.id === orderId);
        if (order) {
          order.item_statuses = item_statuses;
          order.station_progress = station_progress;
          if (status) order.status = status;
          if (order.items && item_statuses) {
            order.items.forEach((item) => {
              const idx = item.index ?? order.items.indexOf(item);
              item.done = item_statuses[idx]?.done || false;
            });
          }
        }
      })

      .addCase(markDelivered.fulfilled, (state, action) => {
        const { orderId, status } = action.payload;
        if (status === 'completed') {
          state.orders = state.orders.filter((o) => o.id !== orderId);
        } else {
          const order = state.orders.find((o) => o.id === orderId);
          if (order) order.status = status;
        }
      })

      .addCase(recallOrder.fulfilled, (state, action) => {
        const order = state.orders.find((o) => o.id === action.payload.orderId);
        if (order) order.status = action.payload.status;
      });
  },
});

export const { upsertOrder, removeOrder, updateOrderItemStatuses, optimisticToggleItem, clearKds } = kdsSlice.actions;
export default kdsSlice.reducer;
